<template lang="pug">
.d-flex.flex-wrap.align-center.gap-2
  .d-flex.flex-wrap.align-center.gap-2.flex-grow-1
    filter-amount(ref="amount" :filter.sync="amountFilter")
    filter-claim-type(ref="claimType" :filter.sync="claimTypeFilter")
    filter-claim-number(ref="claimNumber" :filter.sync="claimNumberFilter")
    filter-panel(ref="panel" :filter.sync="panelFilter")
    filter-claim-date(ref="claimDate" :filter.sync="dateFilter")
  yz-btn(:label="$t('reset_filters')" text, underline, @click="resetFilters" :disabled="!hasActiveFilters")
</template>

<script>
import FilterAmount from '@/components/clinic/panel/claims/filters/FilterAmount.vue'
import FilterClaimType from '@/components/clinic/panel/claims/filters/FilterClaimType.vue'
import FilterClaimNumber from '@/components/clinic/panel/claims/filters/FilterClaimNumber.vue'
import FilterPanel from '@/components/clinic/panel/claims/filters/FilterPanel.vue'
import FilterClaimDate from '@/components/clinic/panel/claims/filters/FilterClaimDate.vue'

export default {
  components: { FilterAmount, FilterClaimType, FilterClaimNumber, FilterPanel, FilterClaimDate },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    amountFilter: {},
    claimTypeFilter: {},
    claimNumberFilter: {},
    panelFilter: {},
    dateFilter: {},
    isInitializing: false
  }),
  computed: {
    allFilters() {
      const filters = {}

      // Collect all active filters
      if (this.amountFilter.key) {
        Object.assign(filters, this.buildFilterParams(this.amountFilter))
      }
      if (this.claimTypeFilter.key) {
        filters[this.claimTypeFilter.key] = this.claimTypeFilter.value
      }
      if (this.claimNumberFilter.key) {
        filters[this.claimNumberFilter.key] = this.claimNumberFilter.value
      }
      if (this.panelFilter.key) {
        filters[this.panelFilter.key] = this.panelFilter.value
      }
      if (this.dateFilter.key) {
        Object.assign(filters, this.buildFilterParams(this.dateFilter))
      }

      return filters
    },
    hasActiveFilters() {
      return Object.keys(this.allFilters).length > 0
    }
  },
  watch: {
    allFilters: {
      handler(newValue) {
        // Don't emit during initialization to avoid circular updates
        if (!this.isInitializing) {
          this.$emit('update:filters', newValue)
          // Also emit serialized version for URL
          const serialized = this.serializeFilters(newValue)
          this.$emit('filters-serialized', serialized)
        }
      },
      deep: true
    },
    filters: {
      handler(newValue) {
        this.initializeFiltersFromProps(newValue)
      },
      immediate: true
    }
  },
  methods: {
    buildFilterParams(filter) {
      // Handle filters that might have multiple keys (like between ranges)
      if (filter.params) {
        return filter.params
      }
      return { [filter.key]: filter.value }
    },
    serializeFilters(filters) {
      // Serialize filters to base64 encoded string
      if (!filters || Object.keys(filters).length === 0) {
        return null
      }
      try {
        const json = JSON.stringify(filters)
        // Use encodeURIComponent to handle unicode characters properly
        return btoa(encodeURIComponent(json))
      } catch (error) {
        console.error('Error serializing filters:', error)
        return null
      }
    },
    deserializeFilters(base64String) {
      // Deserialize base64 encoded string back to filters object
      if (!base64String) {
        return {}
      }
      try {
        const json = decodeURIComponent(atob(base64String))
        return JSON.parse(json)
      } catch (error) {
        console.error('Error deserializing filters:', error)
        // Show toast message to user
        this.$store.dispatch('showMessage', {
          message: this.$t('filter_parameters_corrupted'),
          color: 'warning'
        })
        return {}
      }
    },
    async initializeFiltersFromProps(filters) {
      // Initialize filters from props if provided (e.g., from URL params)
      if (!filters || Object.keys(filters).length === 0) return

      // Set flag to prevent circular updates
      this.isInitializing = true

      try {
        // If filters is a base64 string, deserialize it first
        let filterParams = filters
        if (typeof filters === 'string') {
          filterParams = this.deserializeFilters(filters)
        }

        if (!filterParams || Object.keys(filterParams).length === 0) return

        // Call restoreFromParams on each filter component
        const restorePromises = []

        // Map filter params to corresponding filter components
        if (filterParams.amount !== undefined || filterParams.amount__gt !== undefined || filterParams.amount__lt !== undefined || filterParams.amount__ne !== undefined || (filterParams.amount__gte !== undefined && filterParams.amount__lte !== undefined)) {
          if (this.$refs.amount) restorePromises.push(this.$refs.amount.restoreFromParams(filterParams))
        }
        if (filterParams.type) {
          if (this.$refs.claimType) restorePromises.push(this.$refs.claimType.restoreFromParams(filterParams))
        }
        if (filterParams.rid) {
          if (this.$refs.claimNumber) restorePromises.push(this.$refs.claimNumber.restoreFromParams(filterParams))
        }
        if (filterParams.panel) {
          if (this.$refs.panel) restorePromises.push(this.$refs.panel.restoreFromParams(filterParams))
        }
        if (filterParams.date !== undefined || filterParams.date__gt !== undefined || filterParams.date__gte !== undefined || filterParams.date__lt !== undefined || filterParams.date__lte !== undefined || (filterParams.date__gte !== undefined && filterParams.date__lte !== undefined)) {
          if (this.$refs.claimDate) restorePromises.push(this.$refs.claimDate.restoreFromParams(filterParams))
        }

        await Promise.all(restorePromises)
      } finally {
        // Reset flag after initialization
        this.isInitializing = false
      }
    },
    resetFilters() {
      this.amountFilter = {}
      this.claimTypeFilter = {}
      this.claimNumberFilter = {}
      this.panelFilter = {}
      this.dateFilter = {}

      // Reset all child filter components
      Object.keys(this.$refs).forEach(key => {
        if (this.$refs[key] && typeof this.$refs[key].reset === 'function') {
          this.$refs[key].reset()
        }
      })
    }
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
